import { defineComponent as _defineComponent } from 'vue'
import { resolveComponent as _resolveComponent, createVNode as _createVNode, unref as _unref, mergeProps as _mergeProps, withCtx as _withCtx, createTextVNode as _createTextVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode } from "vue"

const _hoisted_1 = { class: "me-2 d-flex align-center" }

import { computed, watch, ref } from 'vue'
import { useRouter } from 'vue-router'
import { debounce } from 'lodash'

import {
  getSelectStakeholders,
  getSelectAims,
  getSelectCriterions,
  state,
  getSelectModes,
  getProjectTypeFromRoute,
  PROJECT_TYPE_ROUTES,
  loadAll,
  switchToView,
  is1stLevelStakeholdersMode,
  orderByPropName,
} from '@/store'


export default /*@__PURE__*/_defineComponent({
  __name: 'Header',
  props: {
    chartSelectedChart: {}
  },
  setup(__props: any) {

const props = __props

const router = useRouter()

const isActiveRoute = (name: string) => router.currentRoute.value.name === name

// Chart View filters - expose globally in state
if (!state.chartFilters) {
  const alternativesMap = state.data?.alternatives || {}
  const alternatives = Object.values(alternativesMap)
  const firstAlternative = alternatives[0]?.name || 'all-merged'
  state.chartFilters = {
    selectedAlternative: firstAlternative,
    analysisPer: 'criteria',
    selectedCriterion: '',
  }
}

const chartSelectedAlternative = computed({
  get: () => {
    const alternativesMap = state.data?.alternatives || {}
    const alternatives = Object.values(alternativesMap)
    const firstAlternative = alternatives[0]?.name || 'all-merged'
    return state.chartFilters?.selectedAlternative || firstAlternative
  },
  set: (value) => {
    if (state.chartFilters) {
      state.chartFilters.selectedAlternative = value
    }
  }
})

const chartAnalysisPer = computed({
  get: () => state.chartFilters?.analysisPer || 'criteria',
  set: (value) => {
    if (state.chartFilters) {
      state.chartFilters.analysisPer = value
    }
  }
})

const chartSelectedCriterion = computed({
  get: () => state.chartFilters?.selectedCriterion || '',
  set: (value) => {
    if (state.chartFilters) {
      state.chartFilters.selectedCriterion = value
    }
  }
})

const alternativeOptions = computed(() => {
  const alternativesMap = state.data?.alternatives || {};
  const alternatives = Object.values(alternativesMap).map(alt => ({
    title: alt.name,
    value: alt.name,
  }))
  const allOptions = [
    ...alternatives,
    { title: 'All alternatives (merged)', value: 'all-merged' },
    { title: 'All alternatives (separated)', value: 'all-separated' },
  ]
  
  // Filter out "All alternatives" options for crosstab chart
  if (isCrosstabChart.value) {
    return alternatives
  }
  
  return allOptions
})

const analysisPerOptions = [
  { title: 'Criteria', value: 'criteria' },
  { title: 'Stakeholder', value: 'stakeholder' },
]

const clearSearch = () => {
  state.searchInput = ''
}

const debounceInput = debounce((value: string) => {
  state.searchInput = value
}, 200)

const getSelectModesComputed = computed(() => getSelectModes())

const orderedStakeholders = computed(() =>
  orderByPropName(getSelectStakeholders(), 'text')
)
const orderedCriterions = computed(() =>
  orderByPropName(getSelectCriterions(), 'text')
)
const orderedAims = computed(() =>
  orderByPropName(getSelectAims(), 'text')
)

const isArgumentCountChart = computed(
  () => isActiveRoute('chart-view') && props.chartSelectedChart === 'argument-count'
)

const isAlternativesComparisonChart = computed(
  () => isActiveRoute('chart-view') && props.chartSelectedChart === 'alternatives-comparison'
)

const isCrosstabChart = computed(
  () => isActiveRoute('chart-view') && props.chartSelectedChart === 'crosstab'
)

// Initialize selectedAlternative to first alternative when alternatives load
watch(
  () => state.data?.alternatives,
  (alternativesMap) => {
    if (alternativesMap) {
      const alternatives = Object.values(alternativesMap);
      if (alternatives && alternatives.length > 0) {
        if (!state.chartFilters?.selectedAlternative || state.chartFilters.selectedAlternative === 'all-merged') {
          if (state.chartFilters) {
            state.chartFilters.selectedAlternative = alternatives[0].name
          }
        }
      }
    }
  },
  { immediate: true }
)

watch(
  () => state.mode,
  async () => {
    const currentRoute = router.currentRoute.value
    if (getProjectTypeFromRoute(currentRoute)) {
      await loadAll(currentRoute, false)
      return
    }

    const query: Record<string, string> = {}
    if (state.spreadsheet) {
      query[PROJECT_TYPE_ROUTES.GOOGLE_SPREADSHEET] = state.spreadsheet
    } else if (state.project?.name) {
      query[PROJECT_TYPE_ROUTES.ICO] = state.project.name
    }

    if (Object.keys(query).length === 0) {
      await loadAll(currentRoute, false)
      return
    }

    await loadAll(
      {
        ...currentRoute,
        query: {
          ...currentRoute.query,
          ...query,
        },
      },
      false
    )
  }
)

return (_ctx: any,_cache: any) => {
  const _component_v_icon = _resolveComponent("v-icon")!
  const _component_v_btn = _resolveComponent("v-btn")!
  const _component_v_list_item_title = _resolveComponent("v-list-item-title")!
  const _component_v_list_item = _resolveComponent("v-list-item")!
  const _component_v_list = _resolveComponent("v-list")!
  const _component_v_menu = _resolveComponent("v-menu")!
  const _component_v_tooltip = _resolveComponent("v-tooltip")!
  const _component_v_select = _resolveComponent("v-select")!
  const _component_v_toolbar = _resolveComponent("v-toolbar")!
  const _component_v_col = _resolveComponent("v-col")!
  const _component_v_autocomplete = _resolveComponent("v-autocomplete")!
  const _component_v_row = _resolveComponent("v-row")!
  const _component_v_text_field = _resolveComponent("v-text-field")!

  return (_openBlock(), _createBlock(_component_v_row, { class: "table-filters" }, {
    default: _withCtx(() => [
      _createVNode(_component_v_col, {
        class: "flex-grow-0",
        style: {"align-self":"center"}
      }, {
        default: _withCtx(() => [
          _createVNode(_component_v_toolbar, {
            color: "transparent",
            density: "comfortable",
            class: "pr-2 toolbar-filters"
          }, {
            default: _withCtx(() => [
              _createElementVNode("div", _hoisted_1, [
                _createVNode(_component_v_tooltip, {
                  text: "Global view",
                  location: "top"
                }, {
                  activator: _withCtx(({ props: tooltipProps }) => [
                    _createVNode(_component_v_menu, { location: "bottom" }, {
                      activator: _withCtx(({ props: menuProps }) => [
                        _createVNode(_component_v_btn, _mergeProps({
                          icon: "",
                          class: "toolbar-icon-btn"
                        }, { ...tooltipProps, ...menuProps }, {
                          variant: isActiveRoute('global-view') ? 'flat' : 'text',
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (_unref(switchToView)('global-view')))
                        }), {
                          default: _withCtx(() => [
                            _createVNode(_component_v_icon, {
                              icon: 'mdi-table',
                              color: isActiveRoute('global-view') ? 'primary' : 'grey'
                            }, null, 8, ["color"]),
                            _createVNode(_component_v_icon, {
                              icon: 'mdi-chevron-down',
                              color: isActiveRoute('global-view') ? 'primary' : 'grey'
                            }, null, 8, ["color"])
                          ]),
                          _: 2
                        }, 1040, ["variant"])
                      ]),
                      default: _withCtx(() => [
                        _createVNode(_component_v_list, null, {
                          default: _withCtx(() => [
                            (!_unref(is1stLevelStakeholdersMode))
                              ? (_openBlock(), _createBlock(_component_v_list_item, {
                                  key: 0,
                                  onClick: _cache[1] || (_cache[1] = ($event: any) => (_unref(state).globalCardType = 'stakeholder'))
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_list_item_title, null, {
                                      default: _withCtx(() => _cache[13] || (_cache[13] = [
                                        _createTextVNode("By Stakeholder")
                                      ])),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                }))
                              : (_openBlock(), _createBlock(_component_v_list_item, {
                                  key: 1,
                                  onClick: _cache[2] || (_cache[2] = ($event: any) => (_unref(state).globalCardType = 'label'))
                                }, {
                                  default: _withCtx(() => [
                                    _createVNode(_component_v_list_item_title, null, {
                                      default: _withCtx(() => _cache[14] || (_cache[14] = [
                                        _createTextVNode("By Label")
                                      ])),
                                      _: 1
                                    })
                                  ]),
                                  _: 1
                                }))
                          ]),
                          _: 1
                        })
                      ]),
                      _: 2
                    }, 1024)
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_tooltip, {
                  text: "Stakeholder view",
                  location: "top"
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_btn, _mergeProps({
                      icon: "",
                      class: "toolbar-icon-btn"
                    }, props, {
                      variant: isActiveRoute('stakeholder-view') ? 'flat' : 'text',
                      onClick: _cache[3] || (_cache[3] = ($event: any) => (_unref(switchToView)('stakeholder-view')))
                    }), {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          icon: 'mdi-account-multiple',
                          color: isActiveRoute('stakeholder-view') ? 'primary' : 'grey'
                        }, null, 8, ["color"])
                      ]),
                      _: 2
                    }, 1040, ["variant"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_tooltip, {
                  text: "Property view",
                  location: "top"
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_btn, _mergeProps({
                      icon: "",
                      class: "toolbar-icon-btn"
                    }, props, {
                      variant: isActiveRoute('property-view') ? 'flat' : 'text',
                      onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(switchToView)('property-view')))
                    }), {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          icon: 'mdi-format-list-bulleted',
                          color: isActiveRoute('property-view') ? 'primary' : 'grey'
                        }, null, 8, ["color"])
                      ]),
                      _: 2
                    }, 1040, ["variant"])
                  ]),
                  _: 1
                }),
                _createVNode(_component_v_tooltip, {
                  text: "Chart view",
                  location: "top"
                }, {
                  activator: _withCtx(({ props }) => [
                    _createVNode(_component_v_btn, _mergeProps({
                      icon: "",
                      class: "toolbar-icon-btn"
                    }, props, {
                      variant: isActiveRoute('chart-view') ? 'flat' : 'text',
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (_unref(switchToView)('chart-view')))
                    }), {
                      default: _withCtx(() => [
                        _createVNode(_component_v_icon, {
                          icon: 'mdi-chart-bar',
                          color: isActiveRoute('chart-view') ? 'primary' : 'grey'
                        }, null, 8, ["color"])
                      ]),
                      _: 2
                    }, 1040, ["variant"])
                  ]),
                  _: 1
                })
              ]),
              _createVNode(_component_v_select, {
                items: getSelectModesComputed.value,
                "item-title": "text",
                "item-value": "value",
                modelValue: _unref(state).mode,
                "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((_unref(state).mode) = $event)),
                label: "Select mode",
                "hide-details": "",
                class: "ml-6 flex-grow-1 toolbar-select",
                color: "secondary",
                variant: "outlined",
                density: "comfortable",
                style: {"min-width":"200px"}
              }, null, 8, ["items", "modelValue"])
            ]),
            _: 1
          })
        ]),
        _: 1
      }),
      (!isActiveRoute('chart-view'))
        ? (_openBlock(), _createBlock(_component_v_col, {
            key: 0,
            class: "px-6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_autocomplete, {
                        clearable: "",
                        "item-color": "secondary",
                        color: "secondary",
                        label: "Stakeholders",
                        modelValue: _unref(state).selectedStakeholders,
                        "onUpdate:modelValue": _cache[7] || (_cache[7] = ($event: any) => ((_unref(state).selectedStakeholders) = $event)),
                        items: orderedStakeholders.value,
                        "item-title": "text",
                        multiple: "",
                        hint: `${_unref(state).selectedStakeholders.length}/${_unref(getSelectStakeholders).length}`,
                        "persistent-hint": "",
                        autocomplete: "off",
                        id: "selected-stakeholders",
                        density: "comfortable"
                      }, null, 8, ["modelValue", "items", "hint"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_autocomplete, {
                        clearable: "",
                        "item-color": "secondary",
                        color: "secondary",
                        label: "Criteria",
                        modelValue: _unref(state).selectedCriterions,
                        "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((_unref(state).selectedCriterions) = $event)),
                        items: orderedCriterions.value,
                        "item-title": "text",
                        multiple: "",
                        hint: `${_unref(state).selectedCriterions.length}/${_unref(getSelectCriterions).length}`,
                        "persistent-hint": "",
                        autocomplete: "off",
                        id: "selected-criteria",
                        density: "comfortable"
                      }, null, 8, ["modelValue", "items", "hint"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, null, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_autocomplete, {
                        clearable: "",
                        "item-color": "secondary",
                        color: "secondary",
                        label: "Aims",
                        modelValue: _unref(state).selectedAims,
                        "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((_unref(state).selectedAims) = $event)),
                        items: orderedAims.value,
                        "item-title": "text",
                        multiple: "",
                        hint: `${_unref(state).selectedAims.length}/${_unref(getSelectAims).length}`,
                        "persistent-hint": "",
                        autocomplete: "off",
                        id: "selected-aims",
                        density: "comfortable"
                      }, null, 8, ["modelValue", "items", "hint"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (isActiveRoute('chart-view'))
        ? (_openBlock(), _createBlock(_component_v_col, {
            key: 1,
            class: "px-6"
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_row, null, {
                default: _withCtx(() => [
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "4"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_select, {
                        "item-color": "secondary",
                        color: "secondary",
                        label: "Alternative",
                        modelValue: chartSelectedAlternative.value,
                        "onUpdate:modelValue": _cache[10] || (_cache[10] = ($event: any) => ((chartSelectedAlternative).value = $event)),
                        items: alternativeOptions.value,
                        density: "comfortable",
                        variant: "outlined",
                        disabled: isAlternativesComparisonChart.value
                      }, null, 8, ["modelValue", "items", "disabled"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "4"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_select, {
                        "item-color": "secondary",
                        color: "secondary",
                        label: "Analysis per",
                        modelValue: chartAnalysisPer.value,
                        "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((chartAnalysisPer).value = $event)),
                        items: analysisPerOptions,
                        density: "comfortable",
                        variant: "outlined",
                        disabled: isCrosstabChart.value
                      }, null, 8, ["modelValue", "disabled"])
                    ]),
                    _: 1
                  }),
                  _createVNode(_component_v_col, {
                    cols: "12",
                    md: "4"
                  }, {
                    default: _withCtx(() => [
                      _createVNode(_component_v_select, {
                        "item-color": "secondary",
                        color: "secondary",
                        label: "All criteria",
                        modelValue: chartSelectedCriterion.value,
                        "onUpdate:modelValue": _cache[12] || (_cache[12] = ($event: any) => ((chartSelectedCriterion).value = $event)),
                        items: orderedCriterions.value,
                        "item-title": "text",
                        "item-value": "value",
                        clearable: "",
                        density: "comfortable",
                        variant: "outlined",
                        disabled: isArgumentCountChart.value || isAlternativesComparisonChart.value || isCrosstabChart.value
                      }, null, 8, ["modelValue", "items", "disabled"])
                    ]),
                    _: 1
                  })
                ]),
                _: 1
              })
            ]),
            _: 1
          }))
        : _createCommentVNode("", true),
      (!isActiveRoute('chart-view'))
        ? (_openBlock(), _createBlock(_component_v_col, {
            key: 2,
            class: "flex-grow-0",
            style: {"min-width":"200px"}
          }, {
            default: _withCtx(() => [
              _createVNode(_component_v_text_field, {
                color: "secondary",
                label: "Search",
                "model-value": _unref(state).searchInput,
                "onUpdate:modelValue": _unref(debounceInput),
                clearable: "",
                density: "comfortable"
              }, {
                "append-inner": _withCtx(() => [
                  _createVNode(_component_v_icon, {
                    icon: "mdi-magnify",
                    class: "cursor-pointer",
                    onClick: clearSearch
                  })
                ]),
                _: 1
              }, 8, ["model-value", "onUpdate:modelValue"])
            ]),
            _: 1
          }))
        : _createCommentVNode("", true)
    ]),
    _: 1
  }))
}
}

})